import { useState, useRef } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr, API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { Megaphone, Send, Users, Video, Loader2, X } from "lucide-react";
import { fDateTime } from "@/lib/format";
import { toast } from "sonner";

export default function Utskick() {
  const { t } = useLang();
  const { user } = useAuth();
  const canSeePeople = (user?.access_level ?? 1) >= 3;
  const { data: sent, reload } = useApi("/broadcasts");
  const { data: teams } = useApi("/teams");
  const { data: departments } = useApi("/departments");
  const { data: businessAreas } = useApi("/business_areas");
  const { data: shifts } = useApi("/shifts");
  const { data: employees } = useApi(canSeePeople ? "/employees" : null);
  const [form, setForm] = useState({ title: "", message: "", audienceType: "all", audienceId: "" });
  const [video, setVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);

  const empOpts = (employees || []).map((e) => ({ id: e.id, name: `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.email || e.id }));
  const opts = form.audienceType === "business_area" ? businessAreas : form.audienceType === "department" ? departments : form.audienceType === "team" ? teams : form.audienceType === "shift" ? shifts : form.audienceType === "individual" ? empOpts : [];

  const onVideoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVideo({ path: r.data.path });
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); if (videoRef.current) videoRef.current.value = ""; }
  };

  const send = async () => {
    if (!form.message.trim()) return toast.error(t("uts_write_msg"));
    const audience = form.audienceType === "all" ? { type: "all" } : { type: form.audienceType, ids: form.audienceId ? [form.audienceId] : [] };
    setBusy(true);
    try {
      const r = await api.post("/broadcasts", { title: form.title, message: form.message, audience, video_path: video?.path || null });
      toast.success(t("uts_sent", { n: r.data.sent }));
      setForm({ title: "", message: "", audienceType: "all", audienceId: "" });
      setVideo(null);
      reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title={t("uts_title")} icon={Megaphone} subtitle={t("uts_sub")} />

      <Card className="mb-6 grid gap-3 p-4 lg:grid-cols-2">
        <div className="lg:col-span-2"><Label>{t("uts_subject")}</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="bc-title" /></div>
        <div className="lg:col-span-2"><Label>{t("uts_message")}</Label><Textarea rows={3} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder={t("uts_message_ph")} data-testid="bc-message" /></div>

        {video && (
          <div className="relative w-fit lg:col-span-2">
            <video src={`${API}/files/${video.path}`} controls className="max-h-56 rounded-lg border bg-black" />
            <button onClick={() => setVideo(null)} className="absolute -right-2 -top-2 rounded-full bg-background p-1 shadow" data-testid="bc-video-remove"><X className="h-4 w-4" /></button>
          </div>
        )}

        <div><Label>{t("uts_recipient")}</Label>
          <Select value={form.audienceType} onValueChange={(v) => setForm((f) => ({ ...f, audienceType: v, audienceId: "" }))}>
            <SelectTrigger data-testid="bc-aud-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("aud_all")}</SelectItem><SelectItem value="business_area">{t("aud_area")}</SelectItem><SelectItem value="department">{t("aud_dept")}</SelectItem><SelectItem value="team">{t("aud_team")}</SelectItem><SelectItem value="shift">{t("aud_shift")}</SelectItem>{canSeePeople && <SelectItem value="individual">{t("aud_individual")}</SelectItem>}</SelectContent>
          </Select></div>
        {form.audienceType !== "all" &&
          <div><Label>{t("choose")}</Label>
            <Select value={form.audienceId} onValueChange={(v) => setForm((f) => ({ ...f, audienceId: v }))}>
              <SelectTrigger data-testid="bc-aud-target"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{(opts || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select></div>}
        <div className="flex flex-wrap items-center gap-2 lg:col-span-2">
          <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-3 py-2 text-sm hover:bg-secondary" data-testid="bc-video-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />} {t("uts_video")}
            <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={onVideoFile} data-testid="bc-video-input" />
          </label>
          <Button onClick={send} disabled={busy || uploading} data-testid="bc-send"><Send className="mr-1 h-4 w-4" />{busy ? t("uts_sending") : t("uts_send")}</Button>
        </div>
      </Card>

      <div className="mb-2 text-sm font-semibold">{t("uts_sent_heading")}</div>
      {(sent || []).length === 0 ? <EmptyState icon={Megaphone} title={t("uts_empty_title")} text={t("uts_empty_text")} /> :
        <div className="space-y-2">
          {sent.map((b) => (
            <Card key={b.id} className="p-4" data-testid={`bc-${b.id}`}>
              <div className="flex items-center gap-2">
                {b.title && <span className="font-medium">{b.title}</span>}
                <Badge variant="secondary" className="gap-1"><Users className="h-3 w-3" />{b.recipient_count} {t("recipients")}</Badge>
                <span className="ml-auto text-xs text-muted-foreground">{fDateTime(b.created_at)}</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{b.message}</p>
              {b.video_path && <video src={`${API}/files/${b.video_path}`} controls className="mt-2 max-h-72 w-full rounded-lg border bg-black" data-testid={`bc-video-${b.id}`} />}
            </Card>
          ))}
        </div>}
    </div>
  );
}
